import { useEffect, useState, type FormEvent } from 'react';
import { useAuthStore } from '../../auth/store';
import {
  useChangePassword,
  useEnrollTwoFactor,
  useVerifyTwoFactor,
  useDisableTwoFactor,
  useUploadAvatar,
  useRegenerateBackupCodes,
  useBackupCodesCount,
} from '../../api/queries/settings';
import { fetchMe, ApiError } from '../../api/client';
import { PhotoCropModal } from '../../components/PhotoCropModal';
import { UserAvatar } from '../../components/UserAvatar';
import '../SettingsPage.css';

// The user-scoped profile surface: avatar, password, 2FA, backup codes.
// Extracted from SettingsPage so both the admin Settings hub
// (/system-settings) and the personal User Settings page (/settings) can
// render it.

// Raster types the avatar endpoint accepts (mirrors the server allowlist).
// Mirrors the directory contact-photo flow so a stray "All files" pick is
// rejected before we open the cropper.
const PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// ---------- Avatar card (used inside ProfileTab) ----------

function AvatarCard(): JSX.Element {
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const upload = useUploadAvatar();
  const [error, setError] = useState<string | null>(null);
  // A picked file awaiting crop (opens the same <PhotoCropModal> the directory
  // uses), and a cache-buster bumped on successful upload so the <img> reloads
  // even when avatar_path is unchanged on a replace.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [v, setV] = useState(0);

  useEffect(() => { setV((n) => n + 1); }, [me?.user.avatar_updated_at, me?.user.avatar_path]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = ''; // let the same file be re-picked
    if (!f) return;
    setError(null);
    if (!PHOTO_MIMES.includes(f.type)) {
      setError('Choose a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('Avatar must be under 5 MB.');
      return;
    }
    setPendingFile(f);
  };

  // Pick → pan/zoom crop → upload the square JPEG the cropper hands back.
  const onCropConfirm = async (cropped: File) => {
    try {
      await upload.mutateAsync(cropped);
      // Refresh /me so AppShell + display pick up the new path. We're
      // mid-session so /me must return a user — defensively narrow.
      const { user, ...rest } = await fetchMe();
      if (user) setMe({ user, ...rest });
      setV((n) => n + 1);
      setPendingFile(null);
    } catch (err) {
      setError(err instanceof ApiError ? `Upload failed (${err.status})` : 'Upload failed.');
      setPendingFile(null);
    }
  };

  const hasAvatar = me?.user.has_avatar ?? !!me?.user.avatar_path;

  return (
    <div className="card">
      <h3>Profile Photo</h3>
      <p className="set-card-meta">Shown across the app and on your directory (Staff) entry — it's one photo everywhere.</p>
      <div className="set-avatar-row">
        <UserAvatar
          userId={me?.user.id}
          hasAvatar={hasAvatar}
          alt="Your avatar"
          className="set-avatar"
          refreshKey={v}
          fallback={
            <div className="set-avatar set-avatar-placeholder">
              {me?.user.display_name?.charAt(0)?.toUpperCase() ?? '?'}
            </div>
          }
        />
        <label className="btn-charcoal-secondary">
          {upload.isPending ? 'Uploading…' : (hasAvatar ? 'Replace photo' : 'Upload photo')}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={onPick}
            disabled={upload.isPending}
            style={{ display: 'none' }}
          />
        </label>
      </div>
      {error && <p className="set-error" role="alert">{error}</p>}

      {pendingFile && (
        <PhotoCropModal
          file={pendingFile}
          open
          busy={upload.isPending}
          onCancel={() => { if (!upload.isPending) setPendingFile(null); }}
          onConfirm={onCropConfirm}
        />
      )}
    </div>
  );
}

// ---------- Profile tab ----------

export function ProfileTab({ email }: { email: string }): JSX.Element {
  const changePassword = useChangePassword();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwOk, setPwOk] = useState(false);

  const enroll = useEnrollTwoFactor();
  const verify = useVerifyTwoFactor();
  const disable = useDisableTwoFactor();
  const [verifyCode, setVerifyCode] = useState('');
  const [twoFaError, setTwoFaError] = useState<string | null>(null);
  const [twoFaOk, setTwoFaOk] = useState<string | null>(null);
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const onChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setPwOk(false);
    if (newPw.length < 12) {
      setPwError('New password must be at least 12 characters.');
      return;
    }
    try {
      await changePassword.mutateAsync({ current_password: currentPw, new_password: newPw });
      setPwOk(true);
      setCurrentPw('');
      setNewPw('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setPwError('Current password is incorrect.');
        return;
      }
      setPwError('Could not change password. Try again.');
    }
  };

  const onEnroll = async () => {
    setTwoFaError(null);
    setTwoFaOk(null);
    try {
      await enroll.mutateAsync();
    } catch (err) {
      setTwoFaError(err instanceof ApiError ? `Failed (${err.status})` : 'Failed.');
    }
  };

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    setTwoFaError(null);
    try {
      await verify.mutateAsync(verifyCode);
      setTwoFaOk('2FA enabled. Save your backup secret somewhere safe.');
      setVerifyCode('');
    } catch (err) {
      setTwoFaError(err instanceof ApiError && err.status === 401 ? 'Wrong code, try again.' : 'Verification failed.');
    }
  };

  const onDisable = async (e: FormEvent) => {
    e.preventDefault();
    setTwoFaError(null);
    try {
      await disable.mutateAsync({ current_password: disablePw, code: disableCode });
      setTwoFaOk('2FA disabled.');
      setDisablePw('');
      setDisableCode('');
    } catch (err) {
      setTwoFaError(err instanceof ApiError && err.status === 401 ? 'Wrong password or code.' : 'Failed.');
    }
  };

  const enrollData = enroll.data;

  return (
    <div className="set-tab-body">
      <AvatarCard />

      <div className="card">
        <h3>Change Password</h3>
        <form className="set-form" onSubmit={onChangePassword}>
          <label className="set-field">
            <span>Current password</span>
            <input
              type="password"
              className="form-input"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              autoComplete="current-password"
              disabled={changePassword.isPending}
            />
          </label>
          <label className="set-field">
            <span>New password (≥ 12 chars)</span>
            <input
              type="password"
              className="form-input"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              minLength={12}
              required
              autoComplete="new-password"
              disabled={changePassword.isPending}
            />
          </label>
          {pwError && <p className="set-error" role="alert">{pwError}</p>}
          {pwOk && <p className="set-success">Password changed. Other sessions have been logged out.</p>}
          <div className="set-form-actions">
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={changePassword.isPending || newPw.length < 12 || currentPw.length === 0}
            >
              {changePassword.isPending ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <h3>Two-factor authentication</h3>
        <p className="set-card-meta">
          Account: <code>{email}</code>
        </p>
        {!enrollData ? (
          <>
            <p className="set-card-body">
              When enabled, log-in requires a 6-digit code from your
              authenticator app in addition to your password.
            </p>
            <div className="set-form-actions">
              <button
                type="button"
                className="btn-cyan-primary"
                onClick={onEnroll}
                disabled={enroll.isPending}
              >
                {enroll.isPending ? 'Generating…' : 'Enable 2FA'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="set-card-body">
              Scan the QR code with your authenticator app, then enter the 6-digit code below to
              activate.
            </p>
            <img src={enrollData.qr_code_data_url} alt="2FA QR code" className="set-2fa-qr" />
            <p className="set-card-meta">
              Or enter manually: <code>{enrollData.secret_base32}</code>
            </p>
            <form className="set-form" onSubmit={onVerify}>
              <label className="set-field">
                <span>6-digit code from app</span>
                <input
                  type="text"
                  className="form-input set-2fa-code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  disabled={verify.isPending}
                />
              </label>
              <div className="set-form-actions">
                <button
                  type="submit"
                  className="btn-cyan-primary"
                  disabled={verify.isPending || verifyCode.length !== 6}
                >
                  {verify.isPending ? 'Verifying…' : 'Confirm and enable'}
                </button>
              </div>
            </form>
          </>
        )}

        <details className="set-2fa-disable">
          <summary>Disable 2FA</summary>
          <form className="set-form" onSubmit={onDisable}>
            <label className="set-field">
              <span>Current password</span>
              <input
                type="password"
                className="form-input"
                value={disablePw}
                onChange={(e) => setDisablePw(e.target.value)}
                disabled={disable.isPending}
              />
            </label>
            <label className="set-field">
              <span>6-digit code</span>
              <input
                type="text"
                className="form-input set-2fa-code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                disabled={disable.isPending}
              />
            </label>
            <div className="set-form-actions">
              <button
                type="submit"
                className="btn-charcoal-secondary"
                disabled={disable.isPending || disablePw.length === 0 || disableCode.length !== 6}
              >
                {disable.isPending ? 'Disabling…' : 'Disable 2FA'}
              </button>
            </div>
          </form>
        </details>

        {twoFaError && <p className="set-error" role="alert">{twoFaError}</p>}
        {twoFaOk && <p className="set-success">{twoFaOk}</p>}
      </div>

      <BackupCodesCard />
    </div>
  );
}

// ---------- 2FA backup codes card ----------

function BackupCodesCard(): JSX.Element {
  const countQ = useBackupCodesCount();
  const regen = useRegenerateBackupCodes();
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onRegen = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await regen.mutateAsync({ current_password: pw, code });
      setPw('');
      setCode('');
      void countQ.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError('Enable 2FA before issuing backup codes.');
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        setError('Wrong password or 2FA code.');
        return;
      }
      setError('Could not regenerate backup codes.');
    }
  };

  const codes = regen.data?.codes;
  const remaining = countQ.data?.remaining ?? 0;

  return (
    <div className="card">
      <h3>2FA backup codes</h3>
      <p className="set-card-meta">
        Single-use codes you can use instead of your authenticator if you lose your device.
        {' '}
        Currently <strong>{remaining}</strong> unused code{remaining === 1 ? '' : 's'} remaining.
      </p>
      {codes ? (
        <div className="set-backup-codes">
          <p className="set-success">{regen.data!.note}</p>
          <ul className="set-codes-list">
            {codes.map((c) => <li key={c}><code>{c}</code></li>)}
          </ul>
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => {
              const txt = codes.join('\n');
              navigator.clipboard?.writeText(txt).catch(() => {});
            }}
          >
            Copy codes
          </button>
        </div>
      ) : (
        <form className="set-form" onSubmit={onRegen}>
          <p className="set-card-body">Generating new codes invalidates any previous batch.</p>
          <div className="set-form-row">
            <label className="set-field">
              <span>Current password</span>
              <input type="password" className="form-input" value={pw}
                onChange={(e) => setPw(e.target.value)} disabled={regen.isPending} />
            </label>
            <label className="set-field">
              <span>6-digit 2FA code</span>
              <input type="text" className="form-input set-2fa-code" value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric" pattern="\d{6}" maxLength={6} disabled={regen.isPending} />
            </label>
          </div>
          {error && <p className="set-error" role="alert">{error}</p>}
          <div className="set-form-actions">
            <button type="submit" className="btn-cyan-primary"
              disabled={regen.isPending || pw.length === 0 || code.length !== 6}>
              {regen.isPending ? 'Generating…' : 'Generate backup codes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
